import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { SyncingHumanApiServiceServiceProxy, ConnectSessionDto, CreateConnectSessionDto } from '@shared/service-proxies/service-proxies';
import { AuthService } from '../common/IDSVRAuthProviders/auth.service';

declare var HumanConnect: any;

/**
 * What Connect reports back when the user closes the window.
 */
export interface HumanApiConnectResult {
    /** How many sources the user has connected in total, including ones from earlier sessions. */
    connectedSourceCount: number;
    /** Sources connected, disconnected or requested during this session only. */
    sessionResults: any;
    /** Every source the user currently has connected. */
    currentConnections: any[];
}

/**
 * Owns the Human API Connect (v2.3) browser integration.
 *
 * Three things about the Connect client shape this service:
 *
 *  1. HumanConnect.on() appends listeners and offers no way to remove them, so listeners are
 *     registered exactly once for the lifetime of the app.  Registering them per component - which is
 *     what the old global HumanApiConnect() function effectively did - meant every visit to a wearables
 *     screen added another listener and the handler fired once per visit.
 *
 *  2. The client is fetched on demand rather than from a script tag in each index.html.  The URL below
 *     is the single place it is named, and the 96KB download only happens for users who actually open
 *     Connect.
 *
 *  3. The session token is minted by our backend, because signing that call needs the client secret.
 *     Nothing secret reaches the browser any more.
 */
@Injectable({
    providedIn: 'root'
})
export class HumanApiConnectService {

    private static readonly ConnectClientUrl = 'https://cdn.humanapi.co/humanapi-connect-client@latest.js';

    /**
     * All of this describes one global Connect client rather than one Angular instance, so it is held
     * statically.  A per-instance bind flag paired with instance-bound handlers would route a second
     * instance's events into the first instance's pending session.
     */
    private static listenersBound = false;
    private static clientLoad: Promise<void> = null;
    private static pending: Subject<HumanApiConnectResult> = null;

    constructor(
        private _syncingHumanApiServiceServiceProxy: SyncingHumanApiServiceServiceProxy,
        private _authService: AuthService
    ) { }

    /**
     * Loads the Connect client if needed, mints a session token and opens Connect.  Emits once when the
     * user closes the window.
     *
     * @param onOpened Invoked the moment the Connect window is launched, so callers can drop whatever
     *                 busy indicator they were showing while the token was being minted.
     */
    openConnect(onOpened?: () => void): Observable<HumanApiConnectResult> {
        if (HumanApiConnectService.pending) {
            // Connect is already open - the client itself refuses to open a second window, so a second
            // caller would otherwise wait on a result that never arrives.
            return throwError(new Error('A Connect session is already in progress.'));
        }

        const result = new Subject<HumanApiConnectResult>();
        HumanApiConnectService.pending = result;

        this.loadClient()
            .then(() => this.mintTokenAndOpen(onOpened))
            .catch(error => this.fail(error));

        return result.asObservable();
    }

    /**
     * Fetches the Connect client the first time someone opens Connect.  Concurrent callers share the
     * one in-flight load; a failed load is not cached, so the next attempt retries.
     */
    private loadClient(): Promise<void> {
        if (typeof HumanConnect !== 'undefined') {
            return Promise.resolve();
        }

        if (HumanApiConnectService.clientLoad) {
            return HumanApiConnectService.clientLoad;
        }

        HumanApiConnectService.clientLoad = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = HumanApiConnectService.ConnectClientUrl;
            script.async = true;

            script.onload = () => {
                if (typeof HumanConnect === 'undefined') {
                    HumanApiConnectService.clientLoad = null;
                    reject(new Error('The Human API Connect client loaded but did not register itself.'));
                    return;
                }
                resolve();
            };

            script.onerror = () => {
                HumanApiConnectService.clientLoad = null;
                script.parentNode.removeChild(script);
                reject(new Error('The Human API Connect client could not be downloaded.'));
            };

            document.head.appendChild(script);
        });

        return HumanApiConnectService.clientLoad;
    }

    private mintTokenAndOpen(onOpened?: () => void): void {
        this.bindListeners();

        const request = new CreateConnectSessionDto();
        request.accessToken = this._authService.user.access_token;

        this._syncingHumanApiServiceServiceProxy
            .createConnectSessionAsync(request)
            .subscribe((session: ConnectSessionDto) => {
                if (!session || !session.token) {
                    this.fail(new Error('Human API did not return a Connect session token.'));
                    return;
                }

                // The client silently does nothing if one of its iframes is already in the document, so
                // a leftover from a previous session would leave us waiting on a close event that never
                // comes.  Better to say so than to hang the button until the page is reloaded.
                if (document.getElementById('human-api')) {
                    this.fail(new Error('A Human API Connect window is already open.'));
                    return;
                }

                try {
                    // version: 'V2' is what the data-hapi-token markup path passes internally.  Without it
                    // the client falls back to its legacy callback mode and logs a deprecation warning.
                    HumanConnect.open({
                        token: session.token,
                        mode: 'auth',
                        version: 'V2'
                    });
                } catch (error) {
                    // open() throws synchronously on a bad argument.  Without this the pending subject is
                    // never settled and every later attempt is refused as "already in progress".
                    this.fail(error);
                    return;
                }

                if (onOpened) {
                    onOpened();
                }
            }, error => {
                this.fail(error);
            });
    }

    private bindListeners(): void {
        if (HumanApiConnectService.listenersBound) {
            return;
        }
        HumanApiConnectService.listenersBound = true;

        HumanConnect.on('close', (response: any) => {
            const connections = (response && response.currentConnections) || [];
            this.resolve({
                connectedSourceCount: connections.length,
                sessionResults: response ? response.sessionResults : null,
                currentConnections: connections
            });
        });

        HumanConnect.on('error', (response: any) => {
            this.fail(response instanceof Error ? response : new Error('Human API Connect reported an error.'));
        });
    }

    private resolve(result: HumanApiConnectResult): void {
        const pending = HumanApiConnectService.pending;
        HumanApiConnectService.pending = null;
        if (pending) {
            pending.next(result);
            pending.complete();
        }
    }

    private fail(error: any): void {
        const pending = HumanApiConnectService.pending;
        HumanApiConnectService.pending = null;
        if (pending) {
            pending.error(error);
        }
    }
}
